import { LoaderFunction, Params } from 'react-router-dom';

// Graphql
import { client } from '../../../../../gql/client';
import { FETCH_ACCIDENTS } from '../../../../../gql/queries/accidents/accidents';
import { FETCH_FILTER_ACCIDENTS } from '../../../../../gql/queries/accidents/predefinedData';
import { GET_CONCESSIONS } from '../../../../../gql/queries/orders/orders';
import {
  FETCH_CAPITOLS,
  FETCH_SUB_CAPITOLS,
  FETCH_CLASSIFICATIONS,
  FETCH_PREDEFINED_ORDERS,
} from '../../../../../gql/queries/orders/predefinedData';
import { FETCH_TEAMS, FETCH_USERS } from '../../../../../gql/queries/users';
import { selectAccidentOrders } from '../../../../../gql/selectors/accidents';
import {
  prefetchAccidentOptions,
  prefetchWizardCatalogs,
  prefetchAccidentRows,
} from '../../../../../offline/prefetchOfflineData';

// Types
import { AuthContextType } from '../../../../../hooks/useAuth';
import {
  tableIds,
  takeBase,
} from '../../../../../components/display/tables/types';
import { firstPageTake } from '../../../../../utils/cursor-take.helper';

// Redyux
import { store } from '../../../../../state/configureStore';
import { getCursor } from '../../../../../state/selectors/ui/tables';
import { loadingData } from '../../../../../state/actions/ui/loadingData';
import { setCursor } from '../../../../../state/actions/ui/cursorTables';
import { refreshOrderFlags } from '../../../../../gql/services/orderFlags';

/**
 * Calienta la caché de Apollo para que el editor de un accidente abra
 * COMPLETO sin cobertura (2026-08-05).
 *
 * El problema que arregla: sin red, editar un accidente desde el detalle de
 * una OT funcionaba y desde esta tabla no. No era que la tabla trajera menos
 * campos — offline no hay red para nadie y todo depende de qué haya en la
 * caché; lo que pasaba es que el detalle de OT la calienta a propósito y la
 * tabla no. En concreto faltaba `FETCH_VEHICLES`, y como
 * `selectActiveOperatorsAndVehicles` resuelve concesiones+vehículos+usuarios+
 * equipos en un `Promise.all`, con que faltara una caía el lote entero y el
 * desplegable "Registrado por" salía vacío.
 *
 * 🔴 NO se hace `await`: esto va DETRÁS del `defer`, con la tabla ya pintada.
 * El foreground del loader no cambia ni una query — la tabla no puede tardar
 * más en aparecer por esto.
 *
 * El ORDEN sí importa, y es el mismo que usa `WorkOrderDetailed`: catálogos
 * primero, cadenas por fila después. Con los catálogos ya normalizados cada
 * `selectAccidentById` resuelve los suyos por redirect de caché en vez de ir
 * a red.
 */
const warmOfflineCache = (accidents: unknown) => {
  prefetchAccidentOptions()
    .then(() => prefetchWizardCatalogs())
    .then(() =>
      prefetchAccidentRows(
        Array.isArray(accidents)
          ? (accidents as Array<{ accidentID?: string }>)
          : []
      )
    )
    .catch(() => {});
};

// Factoría: el loader global es `makeLoader()` sin args (byte-idéntico al de
// siempre); las pestañas de un LISTADO de módulo pasan `containerTypes` +
// el `tableId` de su cursor dedicado.
//
// `getContainerId` (resolver el container desde `params`) se quedó SIN
// llamadores al retirarse las pestañas del detalle de campaña. Se conserva
// la opción, que es lo que hace reutilizable esta factoría.
//
// Nuevo (pestañas de módulo del listado de Campañas) — `containerTypes` es
// ESTÁTICO (no depende de `params`): la pestaña "Accidents" del listado de
// Campañas filtra por TIPO de origen (`['campaign']`), no por un `:id` de
// la URL. Mismo criterio que `ActionsPage`/`MultimediaPage` (Fase 3 nav
// rework, RV).
export const makeLoader = (
  options: {
    getContainerId?: (params: Params) => string | undefined;
    tableId?: string;
    containerTypes?: string[];
  } = {}
): ((userData: AuthContextType | null) => LoaderFunction) => {
  const { getContainerId, tableId = tableIds.ACCIDENTS, containerTypes } = options;
  return (userData) => async ({ params }) => {
  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  // Banderitas (spec 2026-10-22 § 7.2): en paralelo y sin esperar; nunca lanza.
  void refreshOrderFlags(userData);
  try {
  if (userData?.user) {
    const cursorAccidents = getCursor(tableId)(store.getState());
    const dispatch = store.dispatch;

    const roleActive = userData.user.user.rolActive;

    // containerId contextual (sin llamadores hoy, ver la nota de la factoría).
    // Se resincroniza en CADA run del loader (a diferencia de
    // effectiveConcessionId, que solo se pre-setea si está vacío): quien lo
    // use puede navegar entre containers distintos reutilizando el mismo
    // tableId/cursor, y el containerId debe seguir a la URL en vez de
    // quedarse pegado al del container anterior.
    const containerId = getContainerId?.(params);
    if (containerId && cursorAccidents.containerId !== containerId) {
      dispatch(setCursor(tableId, { ...cursorAccidents, containerId }));
    }

    // containerTypes estático, resincronizado en CADA run del loader (mismo
    // criterio que containerId).
    if (
      containerTypes?.length &&
      JSON.stringify(cursorAccidents.containerTypes) !== JSON.stringify(containerTypes)
    ) {
      dispatch(setCursor(tableId, { ...cursorAccidents, containerTypes }));
    }

    // `containerTypes` estático (campaña) gana sobre el cursor; en la ruta
    // GLOBAL el valor sale del filtro "Origen" user-driven ya persistido en
    // `cursorAccidents` (leído ANTES de los dispatches de arriba).
    const effectiveContainerTypes = containerTypes?.length
      ? containerTypes
      : cursorAccidents.containerTypes;

    // FIX A4: Si concessionId no está seteada, pre-setear si solo hay 1 concesión.
    // Esto evita que la primera carga cree una partición Apollo diferente a las siguientes.
    let effectiveConcessionId = cursorAccidents.concessionId;
    if (!effectiveConcessionId) {
      try {
        const { data: { concessions } } = await client.query({ query: GET_CONCESSIONS });
        if (concessions?.length === 1) {
          effectiveConcessionId = concessions[0].id;
          dispatch(setCursor(tableId, { ...cursorAccidents, concessionId: effectiveConcessionId }));
        }
      } catch { /* si falla, continuar sin concessionId */ }
    }

    // FIX (loader-loading-flag-leak): cierra cualquier flag fugado por un run
    // anterior interrumpido (React Router cancel, hot reload, throw mid-fetch).
    // Sin esto, si el run anterior abrió el flag y no llegó al cierre, este
    // run cae en HIT path y nunca lo cierra → tabla bloqueada permanentemente.
    dispatch(loadingData(tableId, false));

    console.log(`%c[LOADER-ACC] ===== LOADER START ===== Redux cursor=${cursorAccidents.cursor ? cursorAccidents.cursor.slice(0,8)+'…' : "''"} take=${cursorAccidents.take} orderBy=${cursorAccidents.orderBy}`, 'color: purple; font-weight: bold');
    const readAccidents = client.readQuery({
      query: FETCH_ACCIDENTS,
      variables: {
        input: {
          take: cursorAccidents.take,
          cursor: cursorAccidents.cursor,
          concessionId: effectiveConcessionId,
          weatherId: cursorAccidents.weatherId,
          mediumKnowledgeId: cursorAccidents.mediumKnowledgeId,
          orderBy: cursorAccidents.orderBy,
          sortField: cursorAccidents.sortField,
          toDate: cursorAccidents.toDate,
          fromDate: cursorAccidents.fromDate,
          indexType: cursorAccidents.indexType,
          indexExpedition: cursorAccidents.indexExpedition,
          classification: cursorAccidents.classification,
          predefinedOrderId: cursorAccidents.predefinedOrderId,
          capitolId: cursorAccidents.capitolId,
          subCapitolId: cursorAccidents.subCapitolId,
          states: cursorAccidents.states,
          userIds: cursorAccidents.userIds,
          // Fase 4 nav Trabajo/Revisión — filtro "Origen" (tabla GLOBAL,
          // user-driven vía cursor); ahora también acepta `containerTypes`
          // estático (pestaña "Accidents" del listado de Campañas).
          containerTypes: effectiveContainerTypes,
          teamIds: cursorAccidents.teamIds,
          pkInitKm: cursorAccidents.pkInitKm,
          pkInitMeter: cursorAccidents.pkInitMeter,
          pkEndKm: cursorAccidents.pkEndKm,
          pkEndMeter: cursorAccidents.pkEndMeter,
          description: cursorAccidents.description,
          laneCutting: cursorAccidents.laneCutting,
          // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
          onlyFlagged: cursorAccidents.onlyFlagged,
          // GOTCHA (review T1): omitir el campo en vez de null/undefined
          // explícito — un `containerId: null` SÍ entra en la keyArgs de
          // Apollo y partiría la caché histórica sin containerId.
          ...(containerId && { containerId }),
        },
      },
    });

    const pageInfo = readAccidents?.accidents?.pageInfo;

    const totalCount = readAccidents?.accidents?.totalCount;

    const [
      {
        data: { concessions },
      },
      {
        data: { weathers, mediumKnowledges },
      },
      {
        data: { classifications },
      },
      {
        data: { capitols },
      },
      {
        data: { subCapitols },
      },
      {
        data: { predefinedOrders },
      },
      {
        data: { users },
      },
      {
        data: { teams },
      },
    ] = await Promise.all([
      client.query({
        query: GET_CONCESSIONS,
      }),
      client.query({
        query: FETCH_FILTER_ACCIDENTS,
      }),
      client.query({
        query: FETCH_CLASSIFICATIONS,
      }),
      client.query({
        query: FETCH_CAPITOLS,
        fetchPolicy: 'cache-first',
      }),
      client.query({
        query: FETCH_SUB_CAPITOLS,
        fetchPolicy: 'cache-first',
      }),
      client.query({
        query: FETCH_PREDEFINED_ORDERS,
        fetchPolicy: 'cache-first',
      }),
      client.query({
        query: FETCH_USERS,
        fetchPolicy: 'cache-first',
      }),
      client.query({
        query: FETCH_TEAMS,
        fetchPolicy: 'cache-first',
      }),
    ]);

    const filters = {
      mediumKnowledges,
      weathers,
      concessions,
      classifications,
      capitols,
      subCapitols,
      predefinedOrders,
      users,
      teams,
    };
    const isCacheEmpty = JSON.stringify(readAccidents) === '{}';
    const itemCount = readAccidents?.accidents?.accidents?.length ?? 0;
    console.log(`%c[LOADER-ACC] readQuery → ${!readAccidents ? 'NULL' : isCacheEmpty ? 'EMPTY {}' : `HIT ${itemCount} items`} | totalCount=${readAccidents?.accidents?.totalCount} | pageInfo=${JSON.stringify(readAccidents?.accidents?.pageInfo)}`, 'color: purple; font-weight: bold');

    const accidents =
      readAccidents &&
      !isCacheEmpty &&
      (await selectAccidentOrders(readAccidents?.accidents.accidents));
    const combinedData = {
      accidents,
      totalCount,
      filters,
      pageInfo,
      roleActive,
    };

    if (!readAccidents || isCacheEmpty) {
      console.log(`%c[LOADER-ACC] CACHE MISS → NETWORK FETCH cursor=${cursorAccidents.cursor ? cursorAccidents.cursor.slice(0,8)+'…' : "''"} take=${cursorAccidents.take} isCacheEmpty=${isCacheEmpty}`, 'color: red; font-weight: bold');
      dispatch(loadingData(tableId, true));
      try {
      // Tarea #11: respetar el take guardado en cursor (page-size selector).
      // El loader corre fuera de React → no puede usar usePersistedPageSize,
      // así que leemos del cursor que el componente actualizó previamente.
      const effectiveTake = firstPageTake(cursorAccidents.take, takeBase);
      let data;
      if (isCacheEmpty) {
        data = await client.query({
          query: FETCH_ACCIDENTS,
          variables: {
            input: {
              take: effectiveTake,
              cursor: '',
              concessionId: effectiveConcessionId,
              weatherId: cursorAccidents.weatherId,
              mediumKnowledgeId: cursorAccidents.mediumKnowledgeId,
              sortField: cursorAccidents.sortField,
              orderBy: cursorAccidents.orderBy ?? 'desc',
              toDate: cursorAccidents.toDate,
              fromDate: cursorAccidents.fromDate,
              indexType: cursorAccidents.indexType,
              indexExpedition: cursorAccidents.indexExpedition,
              classification: cursorAccidents.classification,
              predefinedOrderId: cursorAccidents.predefinedOrderId,
              capitolId: cursorAccidents.capitolId,
              subCapitolId: cursorAccidents.subCapitolId,
              states: cursorAccidents.states,
              userIds: cursorAccidents.userIds,
              containerTypes: effectiveContainerTypes,
              teamIds: cursorAccidents.teamIds,
              pkInitKm: cursorAccidents.pkInitKm,
              pkInitMeter: cursorAccidents.pkInitMeter,
              pkEndKm: cursorAccidents.pkEndKm,
              pkEndMeter: cursorAccidents.pkEndMeter,
              description: cursorAccidents.description,
              laneCutting: cursorAccidents.laneCutting,
              onlyFlagged: cursorAccidents.onlyFlagged,
              ...(containerId && { containerId }),
            },
          },
        });
        // setCursorData OMITE sortField/orderBy → el merge de cursorTables los preserva
        const setCursorData = {
          take: effectiveTake,
          cursor: '',
          concessionId: effectiveConcessionId,
          weatherId: cursorAccidents.weatherId,
          mediumKnowledgeId: cursorAccidents.mediumKnowledgeId,
          toDate: cursorAccidents.toDate,
          fromDate: cursorAccidents.fromDate,
          indexType: cursorAccidents.indexType,
          indexExpedition: cursorAccidents.indexExpedition,
          classification: cursorAccidents.classification,
          predefinedOrderId: cursorAccidents.predefinedOrderId,
          capitolId: cursorAccidents.capitolId,
          subCapitolId: cursorAccidents.subCapitolId,
          states: cursorAccidents.states,
          userIds: cursorAccidents.userIds,
          // Fase 4 nav Trabajo/Revisión — filtro "Origen" (tabla GLOBAL,
          // user-driven vía cursor); ahora también acepta `containerTypes`
          // estático (pestaña "Accidents" del listado de Campañas).
          containerTypes: effectiveContainerTypes,
          teamIds: cursorAccidents.teamIds,
          pkInitKm: cursorAccidents.pkInitKm,
          pkInitMeter: cursorAccidents.pkInitMeter,
          pkEndKm: cursorAccidents.pkEndKm,
          pkEndMeter: cursorAccidents.pkEndMeter,
          description: cursorAccidents.description,
          laneCutting: cursorAccidents.laneCutting,
          onlyFlagged: cursorAccidents.onlyFlagged,
          // Redux (no Apollo): full-replace de la slice del cursor — a
          // diferencia del gotcha de arriba, aquí SÍ hace falta el valor
          // explícito (incl. undefined en el path global) para no perder el
          // containerId sincronizado al principio del loader.
          containerId,
        };

        dispatch(setCursor(tableId, setCursorData));
      } else {
        data = await client.query({
          query: FETCH_ACCIDENTS,
          variables: {
            input: {
              take: cursorAccidents.take,
              cursor: cursorAccidents.cursor,
              concessionId: effectiveConcessionId,
              weatherId: cursorAccidents.weatherId,
              mediumKnowledgeId: cursorAccidents.mediumKnowledgeId,
              orderBy: cursorAccidents.orderBy ?? 'desc',
              sortField: cursorAccidents.sortField,
              toDate: cursorAccidents.toDate,
              fromDate: cursorAccidents.fromDate,
              indexType: cursorAccidents.indexType,
              indexExpedition: cursorAccidents.indexExpedition,
              classification: cursorAccidents.classification,
              predefinedOrderId: cursorAccidents.predefinedOrderId,
              capitolId: cursorAccidents.capitolId,
              subCapitolId: cursorAccidents.subCapitolId,
              states: cursorAccidents.states,
              userIds: cursorAccidents.userIds,
              containerTypes: effectiveContainerTypes,
              teamIds: cursorAccidents.teamIds,
              pkInitKm: cursorAccidents.pkInitKm,
              pkInitMeter: cursorAccidents.pkInitMeter,
              pkEndKm: cursorAccidents.pkEndKm,
              pkEndMeter: cursorAccidents.pkEndMeter,
              description: cursorAccidents.description,
              laneCutting: cursorAccidents.laneCutting,
              onlyFlagged: cursorAccidents.onlyFlagged,
              ...(containerId && { containerId }),
            },
          },
          fetchPolicy: 'network-only',
        });
      }

      const loading = data.loading;
      const pageInfo = data.data.accidents.pageInfo;
      const totalCount = data.data.accidents.totalCount;
      const networkItemCount = data.data.accidents.accidents?.length ?? 0;
      console.log(`%c[LOADER-ACC] NETWORK RESULT → ${networkItemCount} items | totalCount=${totalCount} | pageInfo=${JSON.stringify(pageInfo)}`, 'color: red');

      const [
        {
          data: { concessions },
        },
        {
          data: { weathers, mediumKnowledges },
        },
        {
          data: { classifications },
        },
        {
          data: { capitols },
        },
        {
          data: { subCapitols },
        },
        {
          data: { predefinedOrders },
        },
        {
          data: { users },
        },
        {
          data: { teams },
        },
      ] = await Promise.all([
        client.query({
          query: GET_CONCESSIONS,
        }),
        client.query({
          query: FETCH_FILTER_ACCIDENTS,
        }),
        client.query({
          query: FETCH_CLASSIFICATIONS,
        }),
        client.query({
          query: FETCH_CAPITOLS,
          fetchPolicy: 'cache-first',
        }),
        client.query({
          query: FETCH_SUB_CAPITOLS,
          fetchPolicy: 'cache-first',
        }),
        client.query({
          query: FETCH_PREDEFINED_ORDERS,
          fetchPolicy: 'cache-first',
        }),
        client.query({
          query: FETCH_USERS,
          fetchPolicy: 'cache-first',
        }),
        client.query({
          query: FETCH_TEAMS,
          fetchPolicy: 'cache-first',
        }),
      ]);

      const filters = {
        mediumKnowledges,
        weathers,
        concessions,
        classifications,
        capitols,
        subCapitols,
        predefinedOrders,
        users,
        teams,
      };

      const accidents = await selectAccidentOrders(
        data.data.accidents.accidents
      );

      const combinedData = {
        accidents,
        totalCount,
        filters,
        pageInfo,
        loading,
        roleActive,
      };
      console.log(`%c[LOADER-ACC] ===== RETURN (MISS path) ===== items=${combinedData.accidents?.length ?? 0} totalCount=${combinedData.totalCount} hasNext=${combinedData.pageInfo?.hasNextPage} hasPrev=${combinedData.pageInfo?.hasPreviousPage}`, 'color: purple');
      warmOfflineCache(accidents);
      return combinedData;
      } finally {
        // FIX (loader-loading-flag-leak): cierra el flag aunque el bloque MISS
        // lance una excepción a mitad (selector throw, query error). Antes,
        // si se lanzaba entre dispatch(true) y dispatch(false), el flag
        // quedaba fugado y la tabla bloqueada permanentemente.
        dispatch(loadingData(tableId, false));
      }
    }

    console.log(`%c[LOADER-ACC] ===== RETURN (HIT path) ===== items=${combinedData.accidents?.length ?? 0} totalCount=${combinedData.totalCount} hasNext=${combinedData.pageInfo?.hasNextPage} hasPrev=${combinedData.pageInfo?.hasPreviousPage}`, 'color: purple');
    warmOfflineCache(accidents);
    return combinedData;
  }
  return {};
  } catch (err) {
    console.log('Loader offline fallback:', err);
    // FIX (loader-loading-flag-leak): defensa en profundidad — si algo entre
    // el dispatch(false) inicial y el catch lanzó (p.ej. error en bootstrap
    // Promise.all antes del MISS branch), aseguramos que el flag esté cerrado.
    store.dispatch(loadingData(tableId, false));
    return { accidents: [], totalCount: 0, filters: { mediumKnowledges: [], weathers: [], concessions: [], classifications: [], capitols: [], subCapitols: [], predefinedOrders: [], users: [] }, pageInfo: { hasNextPage: false, hasPreviousPage: false }, roleActive: userData?.user?.user?.rolActive };
    store.dispatch(loadingData(tableIds.ACCIDENTS, false));
    return { accidents: [], totalCount: 0, filters: { mediumKnowledges: [], weathers: [], concessions: [], classifications: [], capitols: [], subCapitols: [], predefinedOrders: [], users: [], teams: [] }, pageInfo: { hasNextPage: false, hasPreviousPage: false }, roleActive: userData?.user?.user?.rolActive };
  }
  };

  return { data: load() };
};
};

// Loader global (ruta `/work-orders/accidents`) — comportamiento byte-idéntico
// al anterior a la Fase 2 nav Trabajo/Revisión.
export const LoaderAccidentGeneralView = makeLoader();
